'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PickerValue,
  hourOptions,
  meridiemOptions,
  minuteOptions,
  pickerItemHeight,
  clamp,
} from "./timePickerUtils";

type Props = {
  heading: string;
  isOpen: boolean;
  onClose: () => void;
  value: PickerValue;
  onChange: (value: PickerValue) => void;
};

type ColumnProps = {
  label: string;
  options: string[];
  selected: string;
  onSelect: (next: string) => void;
};

const VISIBLE_ITEMS = 5;
const PADDING = Math.floor(VISIBLE_ITEMS / 2) * pickerItemHeight;
const ALIGN_DELAY = 350;

const WheelColumn = ({ label, options, selected, onSelect }: ColumnProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(() =>
    options.indexOf(selected)
  );
  const previewIndexRef = useRef(previewIndex);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const touchScrollingRef = useRef(false);
  const pointerState = useRef<{
    pointerId: number;
    startY: number;
    startScroll: number;
    active: boolean;
  } | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const index = options.indexOf(selected);
    const target = index * pickerItemHeight;
    if (Math.abs(node.scrollTop - target) > 1) {
      node.scrollTo({ top: target });
    }
  }, [options, selected]);

  useEffect(() => {
    previewIndexRef.current = previewIndex;
  }, [previewIndex]);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const nearestIndex = useCallback(() => {
    const node = containerRef.current;
    if (!node) {
      return previewIndexRef.current ?? 0;
    }
    const rawIndex = Math.round(node.scrollTop / pickerItemHeight);
    return clamp(rawIndex, 0, options.length - 1);
  }, [options.length]);

  const commitSelection = useCallback(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }
    const index = nearestIndex();
    const targetOffset = index * pickerItemHeight;
    setPreviewIndex(index);
    const nextValue = options[index];
    if (nextValue !== selected) {
      onSelect(nextValue);
    } else {
      const currentOffset = node.scrollTop;
      if (Math.abs(currentOffset - targetOffset) > 1) {
        node.scrollTo({ top: targetOffset, behavior: "smooth" });
      }
    }
    touchScrollingRef.current = false;
    setSnapEnabled(true);
  }, [nearestIndex, onSelect, options, selected, setSnapEnabled]);

  const scheduleCommit = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(commitSelection, ALIGN_DELAY);
  }, [commitSelection]);

  const handleScroll = () => {
    const node = containerRef.current;
    if (!node) return;
    const index = nearestIndex();
    setPreviewIndex(index);
    touchScrollingRef.current = true;
    scheduleCommit();
  };

  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = () => {
    touchScrollingRef.current = false;
    setSnapEnabled(false);
  };

  const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => {
    if (!touchScrollingRef.current) {
      setSnapEnabled(true);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const step = event.key === "ArrowUp" ? -1 : 1;
      const currentIndex = options.indexOf(selected);
      const nextIndex = clamp(currentIndex + step, 0, options.length - 1);
      onSelect(options[nextIndex]);
    }
  };

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (
    event
  ) => {
    if (event.button !== 0) return;
    if (event.pointerType !== "mouse") {
      pointerState.current = null;
      return;
    }
    const node = containerRef.current;
    if (!node) return;
    node.focus();
    node.setPointerCapture(event.pointerId);
    pointerState.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScroll: node.scrollTop,
      active: true,
    };
  };

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (
    event
  ) => {
    if (event.pointerType !== "mouse") {
      return;
    }
    const node = containerRef.current;
    const state = pointerState.current;
    if (!node || !state?.active) return;
    if (state.pointerId !== event.pointerId) return;
    const delta = state.startY - event.clientY;
    const maxScroll = (options.length - 1) * pickerItemHeight;
    const next = clamp(state.startScroll + delta, 0, maxScroll);
    node.scrollTop = next;
    setPreviewIndex(nearestIndex());
    scheduleCommit();
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") {
      return;
    }
    const node = containerRef.current;
    const state = pointerState.current;
    if (!node || !state) return;
    if (state.pointerId !== event.pointerId) return;
    node.releasePointerCapture(event.pointerId);
    pointerState.current = null;
    commitSelection();
  };

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      const node = containerRef.current;
      if (!node) return;
      event.preventDefault();
      const direction = event.deltaY > 0 ? 1 : -1;
      const nextIndex = clamp(
        previewIndexRef.current + direction,
        0,
        options.length - 1
      );
      if (nextIndex === previewIndexRef.current) {
        return;
      }
      previewIndexRef.current = nextIndex;
      setPreviewIndex(nextIndex);
      node.scrollTop = nextIndex * pickerItemHeight;
      scheduleCommit();
    },
    [options.length, scheduleCommit]
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    node.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel]);

  return (
    <div className="flex-1">
      <div className="mb-1 text-center text-3/4 font-semibold uppercase tracking-wide text-black/60">
        {label}
      </div>
      <div className="relative h-[200px]">
        <div
          ref={containerRef}
          className="absolute inset-0 overflow-y-auto overflow-x-hidden scroll-smooth focus:outline-none"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
            WebkitOverflowScrolling: "touch",
            scrollSnapType: snapEnabled ? "y proximity" : "none",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
          role="listbox"
          tabIndex={0}
          aria-label={label}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          <style jsx>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          <div style={{ height: PADDING }} aria-hidden="true" />
          {options.map((option, index) => {
            const isPreview = index === previewIndex;
            return (
              <div
                key={option}
                role="option"
                aria-selected={isPreview}
                onClick={() => {
                  setPreviewIndex(index);
                  onSelect(option);
                  if (containerRef.current) {
                    containerRef.current.scrollTo({
                      top: index * pickerItemHeight,
                      behavior: "smooth",
                    });
                  }
                }}
                className={`relative flex w-full items-center justify-center text-center transition-colors duration-150 ${
                  isPreview ? "text-black font-semibold" : "text-black/40"
                }`}
                style={{
                  height: pickerItemHeight,
                  lineHeight: `${pickerItemHeight}px`,
                  scrollSnapAlign: "center",
                }}
              >
                {option}
              </div>
            );
          })}
          <div style={{ height: PADDING }} aria-hidden="true" />
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-white via-white/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white via-white/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 top-1/2 h-[56px] -translate-y-1/2 border-y border-[#d9d9d9]/70" />
      </div>
    </div>
  );
};

export function TimePickerModal({
  heading,
  isOpen,
  onClose,
  value,
  onChange,
}: Props) {
  const composedValue = useMemo(() => value, [value]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const { body, documentElement } = document;
    const previous = {
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      paddingRight: body.style.paddingRight,
    };
    const scrollY = window.scrollY;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      body.style.overflow = previous.overflow;
      body.style.overscrollBehavior = previous.overscrollBehavior;
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.style.paddingRight = previous.paddingRight;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[24rem] rounded-lg bg-white p-3 text-black shadow-[0_10px_30px_rgba(0,0,0,0.2)]"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="mb-1.5 text-center text-1 font-semibold leading-tight text-black">
          {heading}
        </p>
        <div className="flex gap-2">
          <WheelColumn
            label="Hour"
            options={hourOptions}
            selected={composedValue.hour}
            onSelect={(hour) => onChange({ ...composedValue, hour })}
          />
          <WheelColumn
            label="Minute"
            options={minuteOptions}
            selected={composedValue.minute}
            onSelect={(minute) => onChange({ ...composedValue, minute })}
          />
          <WheelColumn
            label="AM/PM"
            options={meridiemOptions}
            selected={composedValue.meridiem}
            onSelect={(meridiem) => onChange({ ...composedValue, meridiem })}
          />
        </div>
        <div className="mt-2.5 flex justify-end">
          <button
            type="button"
            className="rounded-full bg-[#0057B8] px-2.5 py-1 text-3/4 font-semibold uppercase tracking-wide text-white hover:bg-[#004999]"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
